import { showError } from '@components/app-error';
import { Button } from '@components/buttons';
import { PaymentType, PriceRow, PurchaseRow } from 'server/types';
import { rpx } from 'client/lib/rpx-client';
import { showDialog } from '@components/dialog';
import { useState } from 'preact/hooks';
import { useAuth } from 'client/lib/auth';
import { intl, useIntl } from 'shared/intl/use-intl';

const store = rpx.subscriptionStatus;
const paymentsStore = rpx.payments;

export const planDescription = (paymentType: PaymentType) =>
  paymentType === 'paymentplan' ? intl('plan') : intl('subscription');

async function cancelPlan({
  purchase,
  price,
  courseId,
  asSeller,
}: {
  purchase: Pick<PurchaseRow, 'id'>;
  price: Pick<PriceRow, 'paymentType'>;
  courseId?: UUID;
  asSeller: boolean;
}) {
  const { paymentType } = price;
  const plan = planDescription(paymentType);
  const body = !courseId
    ? intl(
        'Your {plan:string} stops at the end of this billing period. To keep it, email billing@ruzuku.com.',
        {
          plan,
        },
      )
    : asSeller
      ? intl(
          'You may resume this {plan:string} in the future by re-enrolling the student with the same price point they originally enrolled with and payments will resume from where they left off.',
          {
            plan,
          },
        )
      : intl('You can resume this {plan:string} in the future by re-enrolling, if you like.', {
          plan,
        });

  const confirmed = await showDialog({
    children: body,
    title: intl('Cancel {plan:string}', {
      plan: planDescription(paymentType),
    }),
    confirmButtonText: intl('Cancel {plan:string}', {
      plan,
    }),
    mode: 'warn',
    cancelButtonText: intl('Keep {plan:string}', {
      plan,
    }),
  });

  if (!confirmed) {
    return;
  }

  try {
    await store.cancelPurchase({ purchaseId: purchase.id, courseId });

    // Hacky way to reset all state
    location.reload();
  } catch (err) {
    showError(err);
  }
}

async function resumePlan(props: {
  purchase: Pick<PurchaseRow, 'id'>;
  price: Pick<PriceRow, 'paymentType'>;
}) {
  try {
    const plan = planDescription(props.price.paymentType);

    const previewData = await paymentsStore.previewResumeSubscription({
      purchaseId: props.purchase.id,
    });

    const nextChargeText = previewData.chargesImmediately
      ? 'You will be charged immediately.'
      : `Next charge: ${new Date(previewData.nextChargeDate).toLocaleDateString()}`;

    const paymentPlanText = previewData.paymentsRequired
      ? ` You have made ${previewData.paymentsMade} of ${previewData.paymentsRequired} payments.`
      : '';

    const confirmed = await showDialog({
      children: `Resume this ${plan}? ${nextChargeText}${paymentPlanText}`,
      title: intl('Resume {plan:string}', {
        plan,
      }),
      confirmButtonText: intl('Resume {plan:string}', {
        plan,
      }),
      mode: 'ok',
      cancelButtonText: intl('Cancel'),
    });

    if (!confirmed) {
      return;
    }

    await paymentsStore.resumeSubscription({ purchaseId: props.purchase.id });
    // Reload the page to show the new active subscription
    location.reload();
    // Sleep to prevent button becoming temporarily enabled during reload
    await new Promise(() => {});
  } catch (err) {
    showError(err);
  }
}

export function SubscriptionStatus(props: {
  purchase: Pick<PurchaseRow, 'id' | 'disableAccessWhenCanceled' | 'status'>;
  price: Pick<PriceRow, 'paymentType' | 'allowSelfCancel'>;
  courseId?: UUID;
  allowChanges: boolean;
  asSeller: boolean;
  guideEmail?: string;
}) {
  const { purchase, price, allowChanges, asSeller } = props;
  const { paymentType } = price;
  const { status } = purchase;
  const [isResuming, setIsResuming] = useState(false);
  const auth = useAuth();
  const level = auth.config.coreUser?.level || auth.user?.level;
  const intl = useIntl();
  const isActiveSubscriptionLike = allowChanges && status === 'active';
  const isCanceledSubscriptionLike =
    allowChanges &&
    ['subscription', 'paymentplan'].includes(paymentType) &&
    ['canceled', 'complete'].includes(status) &&
    level === 'superadmin';
  const mayCancel = paymentType === 'subscription' || asSeller || price.allowSelfCancel;
  const contactText = intl.split('<>Contact your guide</> for questions.');

  const handleResume = async () => {
    setIsResuming(true);
    try {
      await resumePlan(props);
    } finally {
      setIsResuming(false);
    }
  };

  return (
    <div class="flex gap-2">
      <span>
        {intl('This {plan:string} is {status:string}.', {
          plan: planDescription(paymentType),
          status,
        })}
      </span>
      {!allowChanges && (
        <span>
          Contact{' '}
          <a href="mailto:billing@ruzuku.com" class="text-indigo-600 underline">
            billing
          </a>{' '}
          for details.
        </span>
      )}
      {isActiveSubscriptionLike && mayCancel && (
        <Button
          class="border border-red-200 text-red-500 rounded-sm px-2 hover:bg-red-50"
          onClick={() => cancelPlan(props)}
        >
          {intl('Cancel {plan:string}', {
            plan: planDescription(paymentType),
          })}
        </Button>
      )}
      {isCanceledSubscriptionLike && (
        <Button
          class="border border-green-200 text-green-600 rounded-sm px-2 hover:bg-green-50"
          onClick={handleResume}
          isLoading={isResuming}
        >
          {intl('Resume {plan:string}', {
            plan: planDescription(paymentType),
          })}
        </Button>
      )}
      {isActiveSubscriptionLike && !mayCancel && !!props.guideEmail && (
        <span>
          {contactText[0]} <a href={`mailto:${props.guideEmail}`}>{contactText[1]}</a>{' '}
          {contactText[2]}
        </span>
      )}
    </div>
  );
}
